import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import Paths from '../../constants/Paths';
import BoardAddPopup from '../BoardAddPopup';
import { Button, ButtonVariant, Icon, IconType, IconSize, ProgressBar, ProgressBarSize } from '../Utils';

import * as gs from '../../global.module.scss';
import * as s from './Project.module.scss';

const Project = React.memo(
  ({ projectId, projects, filteredProjects, managedProjects, isFiltered, isAdmin, isSubscribed, boardTemplates, onBoardCreate, onBoardTemplateUpdate, onBoardTemplateDelete, onProjectMembershipUpdate }) => {
    const [t] = useTranslation();
    const currentFilteredProject = filteredProjects.find((project) => project.id === projectId);
    const currentProject = projects.find((project) => project.id === projectId);
    const isProjectManager = managedProjects.some((p) => p.id === projectId);

    const getBoardsText = () => {
      const boardsCount = currentFilteredProject?.boards.length || 0;
      const totalBoardsCount = currentProject?.boards.length || 0;
      if (!isFiltered) {
        return t('common.boards', { count: boardsCount, context: 'title' });
      }
      return t('common.ofBoards', { filteredCount: boardsCount, count: totalBoardsCount, context: 'title' });
    };

    const handleToggleSubscriptionClick = useCallback(() => {
      onProjectMembershipUpdate(projectId, {
        isSubscribed: !isSubscribed,
      });
    }, [projectId, isSubscribed, onProjectMembershipUpdate]);

    const getBoardCompletionPercent = (board) => {
      if (!board.cardTotal) {
        return null;
      }

      return Math.round((board.cardCompleted / board.cardTotal) * 100);
    };

    return (
      <div className={s.wrapper}>
        <div className={s.pageHeader}>
          <div className={s.pageHeaderMain}>
            <div className={s.pageTitleRow}>
              <h1 className={s.pageTitle} title={currentProject?.name}>
                {currentProject?.name}
              </h1>
              <Button variant={ButtonVariant.Icon} title={isSubscribed ? t('common.unsubscribe') : t('common.subscribe')} onClick={handleToggleSubscriptionClick} className={s.subscribeButton}>
                <Icon type={isSubscribed ? IconType.Bell : IconType.BellEmpty} size={IconSize.Size16} />
              </Button>
            </div>
            <div className={s.pageMeta}>
              <span className={s.pageMetaChip}>{getBoardsText()}</span>
            </div>
          </div>
          <div className={s.headerButtonGroup}>
            {isProjectManager && (
              <BoardAddPopup
                projects={managedProjects}
                projectId={projectId}
                skipProjectDropdown
                isAdmin={isAdmin}
                templates={boardTemplates}
                onCreate={onBoardCreate}
                onTemplateUpdate={onBoardTemplateUpdate}
                onTemplateDelete={onBoardTemplateDelete}
                offset={16}
                position="bottom"
              >
                <Button variant={ButtonVariant.Primary} title={t('common.addBoard')} className={s.addButton}>
                  <Icon type={IconType.Plus} size={IconSize.Size16} className={s.addButtonIcon} />
                  {t('common.addBoard')}
                </Button>
              </BoardAddPopup>
            )}
            {isProjectManager && (
              <Link to={Paths.SETTINGS_PROJECT.replace(':id', projectId)}>
                <Button variant={ButtonVariant.Icon} title={t('common.projectSettings')} className={s.iconAction}>
                  <Icon type={IconType.ProjectSettings} size={IconSize.Size18} />
                </Button>
              </Link>
            )}
            <Link to={Paths.ROOT}>
              <Button variant={ButtonVariant.Icon} title={t('common.backToDashboard')} className={s.iconAction}>
                <Icon type={IconType.ArrowLeftBig} size={IconSize.Size18} />
              </Button>
            </Link>
          </div>
        </div>
        <div className={clsx(s.boardsWrapper, gs.scrollableY, currentProject?.boards.length === 0 && s.boardsWrapperEmpty)}>
          {currentFilteredProject?.boards.map((item) => {
            const completionPercent = getBoardCompletionPercent(item);

            return (
              <div key={item.id} className={s.boardWrapper}>
                {item.notificationsTotal > 0 && <span className={s.notification}>{item.notificationsTotal}</span>}
                <Link to={Paths.BOARDS.replace(':id', item.id)} className={s.boardLink}>
                  <div className={s.board}>
                    <div className={s.boardTop}>
                      <div className={s.boardIconWrap}>
                        <Icon type={IconType.Board} size={IconSize.Size14} className={s.boardIcon} />
                      </div>
                      <div title={item.name} className={s.boardTitle}>
                        {item.name}
                      </div>
                      {completionPercent !== null && <span className={s.boardCompletionBadge}>{completionPercent}%</span>}
                    </div>
                    <div className={s.boardCardStats}>
                      <div className={s.boardStat}>
                        <span className={s.boardStatValue}>{item.cardCompleted}</span>
                        <span className={s.boardStatLabel}>{t('common.done')}</span>
                      </div>
                      <div className={s.boardStat}>
                        <span className={s.boardStatValue}>{item.cardTotal}</span>
                        <span className={s.boardStatLabel}>{t('common.cards', { context: 'title' })}</span>
                      </div>
                    </div>
                    {item.cardTotal > 0 && <ProgressBar value={item.cardCompleted} total={item.cardTotal} size={ProgressBarSize.Tiny} className={s.boardProgressBar} />}
                  </div>
                </Link>
              </div>
            );
          })}
          {currentProject?.boards.length === 0 && isProjectManager && (
            <div className={s.info}>
              <BoardAddPopup
                projects={managedProjects}
                projectId={projectId}
                skipProjectDropdown
                isAdmin={isAdmin}
                templates={boardTemplates}
                onCreate={onBoardCreate}
                onTemplateUpdate={onBoardTemplateUpdate}
                onTemplateDelete={onBoardTemplateDelete}
                offset={16}
                position="bottom"
              >
                <Button variant={ButtonVariant.Primary} title={t('common.addBoard')} className={s.addButton}>
                  <Icon type={IconType.Plus} size={IconSize.Size16} className={s.addButtonIcon} />
                  {t('common.addBoard')}
                </Button>
              </BoardAddPopup>
            </div>
          )}
        </div>
      </div>
    );
  },
);

Project.propTypes = {
  projectId: PropTypes.string.isRequired,
  projects: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  filteredProjects: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  managedProjects: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  isFiltered: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isSubscribed: PropTypes.bool.isRequired,
  boardTemplates: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  onBoardCreate: PropTypes.func.isRequired,
  onBoardTemplateUpdate: PropTypes.func.isRequired,
  onBoardTemplateDelete: PropTypes.func.isRequired,
  onProjectMembershipUpdate: PropTypes.func.isRequired,
};

export default Project;
